import { ChevronDownIcon } from '@chakra-ui/icons';
import {
    Button,
    Flex,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Table,
    Tbody,
    Th,
    Thead,
    Tr,
    Text,
    Image,
} from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { useEffect, useState } from 'react';
import { usePageLocalStorage } from '../../hooks/usePageLocalStorage';
import { RACE_DAY_LICENSE, getGameIcon, MARBLES_LICENSE } from '../../service/games';
import {
    getLeaderboardRanking,
    getLeaderboards,
    leaderboardData,
    rankingData,
} from '../../service/leaderboard';
import { getGameName } from '../../service/lobby';
import { getPublicProfiles, getPublicProfilesByTwitchID, UserProfile } from '../../service/social';
import { LeaderboardRow } from './LeaderboardRow';

interface UserLeaderBoardProps {
    license: string;
    userProfile: UserProfile;
}

export const UserLeaderboard: React.FC<UserLeaderBoardProps> = ({ license, userProfile }) => {
    const { keycloak } = useKeycloak();

    const [profileMap, setProfileMap] = usePageLocalStorage<{ [id: string]: UserProfile }>(
        'profileMap',
        {},
    );
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [leaderboardInfo, setLeaderboardInfo] = useState<leaderboardData[]>([]);
    const [leaderboardMap, setLeaderboardMap] = useState<Record<string, rankingData[]>>({});
    const [ownerIds, setOwnerIds] = useState<string[]>([]);
    const [activeLeaderboard, setActiveLeaderboard] = useState<string>('');

    const getLeaderboardDisplayName = (nameInput: string) => {
        if (license === RACE_DAY_LICENSE) {
            if (nameInput.startsWith(RACE_DAY_LICENSE)) {
                return `${userProfile.username} - all time`;
            } else {
                return `${userProfile.username} - season ${nameInput[0]}`;
            }
        }

        return nameInput;
    };

    useEffect(() => {
        const fetchData = async () => {
            const data = await getLeaderboards(license);
            switch (license) {
                case RACE_DAY_LICENSE:
                    const lbData = data.filter((d) => d.name.includes(userProfile.id));
                    setLeaderboardInfo(lbData);
                    break;
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        const getRanks = async () => {
            let leaderboardData: Record<string, rankingData[]> = {};
            let owners: string[] = [];

            leaderboardInfo.map(async (lb) => {
                const leaderboardRanks = await getLeaderboardRanking(license, lb.name, 'all', '');
                if (leaderboardRanks.length > 0) {
                    leaderboardData[lb.name] = leaderboardRanks;
                    const userIds = leaderboardRanks.map((rank) => rank.id);
                    userIds.map((uid) => {
                        if (!owners.includes(uid)) {
                            owners.push(uid);
                        }
                    });
                }

                setOwnerIds((existing) => ({
                    ...existing,
                    ...owners,
                }));
                setLeaderboardMap((existing) => ({
                    ...existing,
                    ...leaderboardData,
                }));
            });
        };

        getRanks();
    }, [leaderboardInfo]);

    useEffect(() => {
        const fetchProfiles = async () => {
            const stupidJavascriptArrayFix = Object.entries(ownerIds).map((oid) => oid[1]);
            if (Object.entries(ownerIds).length > 0) {
                const profileData = await (license === MARBLES_LICENSE
                    ? getPublicProfilesByTwitchID(stupidJavascriptArrayFix)
                    : getPublicProfiles(stupidJavascriptArrayFix));

                setProfileMap(profileData);
                setActiveLeaderboard(Object.keys(leaderboardMap)[0]);
            }

            setIsLoading(false);
        };

        if (ownerIds) {
            fetchProfiles();
        }
    }, [leaderboardMap, ownerIds]);

    if (isLoading) {
        return <Text>Loading...</Text>;
    } else if (activeLeaderboard === '') {
        return <></>;
    } else {
        return (
            <Flex className={'user-leaderboard'}>
                <Flex className={'game-info'}>
                    <Image src={getGameIcon(license)} />
                    <Text>{getGameName(license)}</Text>
                </Flex>
                {leaderboardMap && (
                    <Menu>
                        {({ isOpen }) => (
                            <>
                                <MenuButton
                                    isActive={isOpen}
                                    as={Button}
                                    rightIcon={<ChevronDownIcon />}
                                >
                                    {activeLeaderboard === '' ? (
                                        <Text>Select a Leaderboard</Text>
                                    ) : (
                                        <Text>{getLeaderboardDisplayName(activeLeaderboard)}</Text>
                                    )}
                                </MenuButton>
                                <MenuList>
                                    {Object.entries(leaderboardMap).map(([name, ranks]) => (
                                        <MenuItem
                                            key={name}
                                            onClick={() => setActiveLeaderboard(name)}
                                        >
                                            {getLeaderboardDisplayName(name)}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </>
                        )}
                    </Menu>
                )}
                {activeLeaderboard !== '' && (
                    <Table className="leaderboard-table">
                        <Thead>
                            <Tr>
                                <Th className="leaderboard-heading">RANK</Th>
                                <Th className="leaderboard-heading">USER</Th>
                                <Th className="leaderboard-heading">SCORE</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {leaderboardMap[activeLeaderboard].map((rnk, idx) => (
                                <LeaderboardRow
                                    key={idx}
                                    rankData={rnk}
                                    profile={profileMap[rnk.id]}
                                    isOddRow={idx % 2 === 0}
                                    activeUserId={keycloak.subject || ''}
                                />
                            ))}
                        </Tbody>
                    </Table>
                )}
            </Flex>
        );
    }
};
